import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api from '../lib/api';
import {
  FIREBASE_AUTH_ENABLED,
  clearFirebaseClientSession,
  loginWithFirebaseSession,
} from '../lib/firebaseAuth';
import {
  clearLifecycleIdempotencyKey,
  registrationIdempotencyKey,
  requireExactRegistrationResponse,
  shouldClearLifecycleIdempotencyKey,
} from '../lib/lifecycleIdempotency';

const AuthContext = createContext(null);

function cacheUser(user) {
  try {
    localStorage.removeItem('token');
    if (user) localStorage.setItem('user', JSON.stringify(user));
    else localStorage.removeItem('user');
  } catch { /* The server cookie, not browser storage, owns authentication. */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let requestId = 0;
    const refresh = () => {
      const current = ++requestId;
      setLoading(true);
      api.get('/auth/me')
      .then((res) => {
        if (cancelled || current !== requestId) return;
        cacheUser(res.data.user);
        setUser(res.data.user);
      })
      .catch(() => {
        if (cancelled || current !== requestId) return;
        cacheUser(null);
        setUser(null);
      })
      .finally(() => {
        if (!cancelled && current === requestId) setLoading(false);
      });
    };
    // Cookie authentication is authoritative. Never trust another tab's cached
    // user object, but hide this tab's old cart while checking the new session.
    const onStorage = event => { if (event.key === 'user' || event.key === null) refresh(); };
    refresh();
    window.addEventListener('storage', onStorage);
    return () => { cancelled = true; window.removeEventListener('storage', onStorage); };
  }, []);

  const login = useCallback(async (email, password) => {
    const authenticatedUser = FIREBASE_AUTH_ENABLED
      ? await loginWithFirebaseSession(api, email, password)
      : (await api.post('/auth/login', { email, password })).data.user;
    cacheUser(authenticatedUser);
    setUser(authenticatedUser);
    return authenticatedUser;
  }, []);

  const register = useCallback(async (data) => {
    const actorId = String(data.email || '').trim().toLowerCase();
    const routeKind = data.is_guest ? 'guest_registration' : 'vip_registration';
    const idempotencyKey = await registrationIdempotencyKey({
      email: actorId, routeKind,
    });
    let res;
    try {
      res = await api.post('/auth/register', data, {
        headers: { 'Idempotency-Key': idempotencyKey },
      });
      requireExactRegistrationResponse(
        res.data, actorId, data.is_guest ? 'guest' : ['pending', 'approved']
      );
    } catch (error) {
      if (shouldClearLifecycleIdempotencyKey(error)) {
        clearLifecycleIdempotencyKey({ routeKind, actorId });
      }
      throw error;
    }
    clearLifecycleIdempotencyKey({ routeKind, actorId });
    if (data.is_guest) {
      cacheUser(res.data.user);
      setUser(res.data.user);
      return res.data.user;
    }
    return { pending: res.data.user.status === 'pending', approved: res.data.user.status === 'approved', message: res.data.message };
  }, []);

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch (e) { /* clear locally regardless */ }
    await clearFirebaseClientSession();
    cacheUser(null);
    setUser(null);
  }, []);

  const updateUser = useCallback((updatedUser) => {
    setUser(updatedUser);
    cacheUser(updatedUser);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, updateUser }),
    [user, loading, login, register, logout, updateUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
